import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {REQUESTS_V2_ANALYTICS_ACTIONS} from 'constants/analytics-products/RequestsV2AnalyticsConstants';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import * as NavConstants from 'constants/NavConstants';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import * as Colors from 'ui-library/Colors';
import SectionTabs, {SectionTab} from 'ui-library/SectionTabs';
import {assertNever} from 'util/Types';

import RequestV2DefinitionListing from './RequestV2DefinitionListing';
import RequestsV2SubmissionListing from './RequestV2SubmissionListing';

type ValidSections =
  | NavConstants.Section.REQUESTS_V2_DEFINITIONS
  | NavConstants.Section.REQUESTS_V2_SUBMISSIONS;

interface SidepanelProps {
  activeSection: ValidSections;
  activeItemId: string | null;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  isExpanded: boolean;
}

export default function RequestsV2Sidepanel(props: SidepanelProps): JSX.Element {
  const tabs: Array<SectionTab> = [
    {
      handleClick: () => {
        analytics.trackEvent(REQUESTS_V2_ANALYTICS_ACTIONS.CLICKED_REQUESTS_V2_REQUEST_TYPES_TAB, {
          action_origin: ActionOrigin.REQUEST_V2_SIDEPANEL,
          action_method: ActionMethod.BUTTON_CLICK,
          is_expanded: props.isExpanded,
        });
        SidepanelActions.openSection(NavConstants.Section.REQUESTS_V2_DEFINITIONS);
      },
      ariaControls: `${NavConstants.Section.REQUESTS_V2_DEFINITIONS}_PANEL`,
      id: `${NavConstants.Section.REQUESTS_V2_DEFINITIONS}_TAB`,
      isActive: props.activeSection === NavConstants.Section.REQUESTS_V2_DEFINITIONS,
      text: 'Request Types',
      dataTestAttrs: {key: 'types'},
    },
    {
      handleClick: () => {
        analytics.trackEvent(REQUESTS_V2_ANALYTICS_ACTIONS.CLICKED_REQUESTS_V2_DASHBOARD_TAB, {
          action_origin: ActionOrigin.REQUEST_V2_SIDEPANEL,
          action_method: ActionMethod.BUTTON_CLICK,
          is_expanded: props.isExpanded,
        });
        SidepanelActions.openSection(NavConstants.Section.REQUESTS_V2_SUBMISSIONS);
      },
      ariaControls: `${NavConstants.Section.REQUESTS_V2_SUBMISSIONS}_PANEL`,
      id: `${NavConstants.Section.REQUESTS_V2_SUBMISSIONS}_TAB`,
      isActive: props.activeSection === NavConstants.Section.REQUESTS_V2_SUBMISSIONS,
      text: 'Dashboard',
      dataTestAttrs: {key: 'dashboard'},
    },
  ];

  const requestsV2Tab = <SectionTabs className={css(styles.sectionTabs)} tabs={tabs} />;

  switch (props.activeSection) {
    case NavConstants.Section.REQUESTS_V2_SUBMISSIONS:
      return (
        <RequestsV2SubmissionListing
          isCompactDisplay={props.isCompactDisplay}
          setIsCompactDisplay={props.setIsCompactDisplay}
          isExpanded={props.isExpanded}
          activeItemId={props.activeItemId}
          requestsV2Tab={requestsV2Tab}
        />
      );
    case NavConstants.Section.REQUESTS_V2_DEFINITIONS:
      return (
        <RequestV2DefinitionListing
          isCompactDisplay={props.isCompactDisplay}
          setIsCompactDisplay={props.setIsCompactDisplay}
          isExpanded={props.isExpanded}
          activeItemId={props.activeItemId}
          requestsV2Tab={requestsV2Tab}
        />
      );
    default:
      assert(false, `Invalid section: ${assertNever(props.activeSection)}`);
      return <div />;
  }
}
const styles = StyleSheet.create({
  sectionTabs: {
    borderBottomColor: Colors.GRAY,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    margin: '0 -12px',
  },
});
